import { FC, useRef, useState, useEffect } from 'react';
import { TableBody, TableHead, TableRow, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import {
  StyledTableContainer,
  HeaderCell,
  SubHeaderCell,
  StyledPaper,
  StyledTable,
  DataCell,
  StatusCell,
  QuarterFirstCell,
  ClosedDataCell,
  ClosedStatusCell,
  ClosedQuarterFirstCell,
  TableWrapper,
  TableTitle,
  ActionButton,
  debounce,
  calculateTextWidth
} from './StatusTable.styled';

interface QuarterData {
  ppo: string;
  date: string;
  version: string | number;
  status: string;
}

interface StatusTableProps {
  data: {
    ppo: string;
    year: string | number;
    version?: string | number;
    status?: string;
    closed?: boolean;
    quarters: {
      q1?: QuarterData;
      q2?: QuarterData;
      q3?: QuarterData;
      q4?: QuarterData;
    };
  }[];
  title?: string;
  showQuarters?: ('q1' | 'q2' | 'q3' | 'q4')[];
  backgroundColor?: string;
}

export const StatusTable: FC<StatusTableProps> = ({ 
  data, 
  title = "Inbound to DKA",
  showQuarters = ['q1', 'q2', 'q3', 'q4'],
  backgroundColor
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [isCalculating, setIsCalculating] = useState(true);

  // Calculate the total number of columns
  const staticColumns = 2; // PPO, Year
  const quarterColumns = 4; // PPO, Date, Version, Status per quarter
  const visibleQuarters = showQuarters.length;
  const totalColumns = staticColumns + (quarterColumns * visibleQuarters);

  // Calculate optimal column widths based on content
  const calculateColumnWidths = () => {
    if (!tableContainerRef.current || data.length === 0) {
      setIsCalculating(false);
      return;
    }

    const newColumnWidths: Record<string, number> = {};
    const headerFont = '700 12px "Lato", sans-serif';
    const cellFont = '400 12px "Lato", sans-serif';

    // Define min and max column widths
    const minColWidth = isMobile ? 20 : 30;
    const maxColWidth = isMobile ? 150 : 200;

    // Basic columns
    newColumnWidths['ppo'] = Math.max(calculateTextWidth('PPO', headerFont) + 8, minColWidth);
    newColumnWidths['year'] = Math.max(calculateTextWidth('Year', headerFont) + 8, minColWidth);

    // Quarter columns
    const quarterHeaders = ['Version', 'Status', 'PPO', 'Date'];
    quarterHeaders.forEach(header => {
      newColumnWidths[header.toLowerCase()] = Math.max(calculateTextWidth(header, headerFont) + 8, minColWidth);
    });

    // Sample data to get better width estimates
    data.slice(0, 10).forEach(row => {
      // Check PPO and Year columns
      newColumnWidths['ppo'] = Math.max(
        newColumnWidths['ppo'], 
        calculateTextWidth(String(row.ppo), cellFont) + 8
      );
      newColumnWidths['year'] = Math.max(
        newColumnWidths['year'], 
        calculateTextWidth(String(row.year), cellFont) + 8
      );

      // Check quarter data
      showQuarters.forEach(quarter => {
        const quarterData = row.quarters[quarter];
        if (quarterData) {
          newColumnWidths['version'] = Math.max(
            newColumnWidths['version'], 
            calculateTextWidth(String(quarterData.version), cellFont) + 8
          );
          newColumnWidths['status'] = Math.max(
            newColumnWidths['status'], 
            calculateTextWidth(String(quarterData.status), cellFont) + 8
          );
          newColumnWidths['ppo'] = Math.max(
            newColumnWidths['ppo'], 
            calculateTextWidth(String(quarterData.ppo), cellFont) + 8
          );
          newColumnWidths['date'] = Math.max(
            newColumnWidths['date'], 
            calculateTextWidth(String(quarterData.date), cellFont) + 8
          );
        }
      });
    });

    // Apply min/max constraints
    Object.keys(newColumnWidths).forEach(key => {
      newColumnWidths[key] = Math.min(Math.max(newColumnWidths[key], minColWidth), maxColWidth);
    });

    // Convert numeric widths to strings with 'px' suffix
    const finalColumnWidths: Record<string, string> = {};
    for (const [key, value] of Object.entries(newColumnWidths)) {
      finalColumnWidths[key] = `${value}px`;
    }

    setColumnWidths(finalColumnWidths);
    setIsCalculating(false);
  };

  // Debounced version of the calculation function
  const debouncedCalculateColumnWidths = debounce(calculateColumnWidths, 300);

  // Calculate column widths on initial render and when data changes
  useEffect(() => {
    setIsCalculating(true);
    calculateColumnWidths();
  }, [data, showQuarters]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      debouncedCalculateColumnWidths();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Function to get status label
  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'D': return 'D';
      case 'U': return 'U';
      case 'A': return 'A';
      case 'R': return 'R';
      default: return status;
    }
  };

  return (
    <TableWrapper>
      <TableTitle>
        {title}
      </TableTitle>
      
      <StyledPaper>
        <StyledTableContainer ref={tableContainerRef}>
          <StyledTable size='small' aria-label='status table' sx={{ borderCollapse: 'separate', borderSpacing: '1px' }}>
            <TableHead>
              <TableRow sx={{ 
                '& th': { 
                  px: 1.5, 
                  py: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1300,
                  backgroundColor: '#E7EFF5 !important',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                } 
              }}>
                {/* Static columns with rowspan */}
                <HeaderCell align='center' rowSpan={3} width={columnWidths['ppo']}>DO</HeaderCell>
                <HeaderCell align='center' rowSpan={3} width={columnWidths['year']}>Year</HeaderCell>
                
                {/* Quarter headers */}
                {showQuarters.includes('q1') && (
                  <HeaderCell align='center' colSpan={4}>Q1</HeaderCell>
                )}
                {showQuarters.includes('q2') && (
                  <HeaderCell align='center' colSpan={4}>Q2</HeaderCell>
                )}
                {showQuarters.includes('q3') && (
                  <HeaderCell align='center' colSpan={4}>Q3</HeaderCell>
                )}
                {showQuarters.includes('q4') && (
                  <HeaderCell align='center' colSpan={4}>Q4</HeaderCell>
                )}
              </TableRow>
              <TableRow sx={{ 
                '& th': { 
                  px: 1.5, 
                  py: 0.75,
                  position: 'sticky',
                  top: '24px',
                  zIndex: 1299,
                  backgroundColor: '#E7EFF5 !important',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                } 
              }}>
                {/* Latest Activity subheaders */}
                {showQuarters.includes('q1') && (
                  <>
                    <HeaderCell align='center' colSpan={2}></HeaderCell>
                    <HeaderCell align='center' colSpan={2}>Latest Activity</HeaderCell>
                  </>
                )}
                {showQuarters.includes('q2') && (
                   <>
                   <HeaderCell align='center' colSpan={2}></HeaderCell>
                   <HeaderCell align='center' colSpan={2}>Latest Activity</HeaderCell>
                 </>
                )}
                {showQuarters.includes('q3') && (
                   <>
                   <HeaderCell align='center' colSpan={2}></HeaderCell>
                   <HeaderCell align='center' colSpan={2}>Latest Activity</HeaderCell>
                 </>
                )}
                {showQuarters.includes('q4') && (
                  <>
                    <HeaderCell align='center' colSpan={2}></HeaderCell>
                    <HeaderCell align='center' colSpan={2}>Latest Activity</HeaderCell>
                  </>
                )}
              </TableRow>
              <TableRow sx={{ 
                '& th': { 
                  px: 1.5, 
                  py: 0.75,
                  position: 'sticky',
                  top: '48px',
                  zIndex: 1298,
                  backgroundColor: '#E7EFF5 !important',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                } 
              }}>
                {/* Column headers for each quarter */}
                {showQuarters.includes('q1') && (
                  <>
                    <SubHeaderCell width={columnWidths['version']}>Version</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['status']}>Status</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['ppo']}>DO</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['date']}>Date</SubHeaderCell>
                  </>
                )}
                {showQuarters.includes('q2') && (
                  <>
                    <SubHeaderCell width={columnWidths['version']}>Version</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['status']}>Status</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['ppo']}>DO</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['date']}>Date</SubHeaderCell>
                  </>
                )}
                {showQuarters.includes('q3') && (
                  <>
                    <SubHeaderCell width={columnWidths['version']}>Version</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['status']}>Status</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['ppo']}>DO</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['date']}>Date</SubHeaderCell>
                  </>
                )}
                {showQuarters.includes('q4') && (
                  <>
                    <SubHeaderCell width={columnWidths['version']}>Version</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['status']}>Status</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['ppo']}>DO</SubHeaderCell>
                    <SubHeaderCell width={columnWidths['date']}>Date</SubHeaderCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {
                // Determine which cell components to use based on closed status
                const CellComponent = row.closed ? ClosedDataCell : DataCell;
                const StatusCellComponent = row.closed ? ClosedStatusCell : StatusCell;
                const QuarterFirstCellComponent = row.closed ? ClosedQuarterFirstCell : QuarterFirstCell;
                
                // Create base cell props with background color (for non-status cells)
                const baseCellProps = backgroundColor ? { sx: { backgroundColor } } : {};
                
                // For status cells, we need to let the styled component handle the color logic
                // The backgroundColor will be used as fallback in the styled components
                
                return (
                  <TableRow key={index} sx={{ '& td': { px: 1.5, py: 0.75 } }}>
                    {/* Static columns */}
                    <CellComponent {...baseCellProps}>{row.ppo}</CellComponent>
                    <CellComponent {...baseCellProps}>{row.year}</CellComponent>
                    
                    {/* Q1 data */}
                    {showQuarters.includes('q1') && row.quarters.q1 && (
                      <>
                        <CellComponent {...baseCellProps}>{row.quarters.q1.version}</CellComponent>
                        <StatusCellComponent status={row.quarters.q1.status} fallbackBackgroundColor={backgroundColor}>{getStatusLabel(row.quarters.q1.status)}</StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}>{row.quarters.q1.ppo}</QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}>{row.quarters.q1.date}</CellComponent>
                      </>
                    )}
                    {showQuarters.includes('q1') && !row.quarters.q1 && (
                      <>
                        <CellComponent {...baseCellProps}></CellComponent>
                        <StatusCellComponent status={undefined} fallbackBackgroundColor={backgroundColor}></StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}></QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}></CellComponent>
                      </>
                    )}
                    
                    {/* Q2 data */}
                    {showQuarters.includes('q2') && row.quarters.q2 && (
                      <>
                        <CellComponent {...baseCellProps}>{row.quarters.q2.version}</CellComponent>
                        <StatusCellComponent status={row.quarters.q2.status} fallbackBackgroundColor={backgroundColor}>{getStatusLabel(row.quarters.q2.status)}</StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}>{row.quarters.q2.ppo}</QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}>{row.quarters.q2.date}</CellComponent>
                      </>
                    )}
                    {showQuarters.includes('q2') && !row.quarters.q2 && (
                      <>
                        <CellComponent {...baseCellProps}></CellComponent>
                        <StatusCellComponent status={undefined} fallbackBackgroundColor={backgroundColor}></StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}></QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}></CellComponent>
                      </>
                    )}
                    
                    {/* Q3 data */}
                    {showQuarters.includes('q3') && row.quarters.q3 && (
                      <>
                        <CellComponent {...baseCellProps}>{row.quarters.q3.version}</CellComponent>
                        <StatusCellComponent status={row.quarters.q3.status} fallbackBackgroundColor={backgroundColor}>{getStatusLabel(row.quarters.q3.status)}</StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}>{row.quarters.q3.ppo}</QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}>{row.quarters.q3.date}</CellComponent>
                      </>
                    )}
                    {showQuarters.includes('q3') && !row.quarters.q3 && (
                      <>
                        <CellComponent {...baseCellProps}></CellComponent>
                        <StatusCellComponent status={undefined} fallbackBackgroundColor={backgroundColor}></StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}></QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}></CellComponent>
                      </>
                    )}
                    
                    {/* Q4 data */}
                    {showQuarters.includes('q4') && row.quarters.q4 && (
                      <>
                        <CellComponent {...baseCellProps}>{row.quarters.q4.version}</CellComponent>
                        <StatusCellComponent status={row.quarters.q4.status} fallbackBackgroundColor={backgroundColor}>{getStatusLabel(row.quarters.q4.status)}</StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}>{row.quarters.q4.ppo}</QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}>{row.quarters.q4.date}</CellComponent>
                      </>
                    )}
                    {showQuarters.includes('q4') && !row.quarters.q4 && (
                      <>
                        <CellComponent {...baseCellProps}></CellComponent>
                        <StatusCellComponent status={undefined} fallbackBackgroundColor={backgroundColor}></StatusCellComponent>
                        <QuarterFirstCellComponent {...baseCellProps}></QuarterFirstCellComponent>
                        <CellComponent {...baseCellProps}></CellComponent>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </StyledPaper>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.75rem', color: '#666' }}>
        (*) Status: D = file version downloaded, U = file version uploaded, A = file version accepted, R = file version marked for removal
      </Typography>
    </TableWrapper>
  );
};
